import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import {
  useCreateVoiceWebhook,
  useVoiceWebhookBodyParameters,
} from '../api';
import { buildInitialBodyTable } from '../payload';
import { VoiceWebhookForm, type VoiceWebhookFormValues } from './VoiceWebhookForm';

export function AddVoiceWebhookPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canAddAcl = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.ADD);
  const canAdd = role !== USER_ROLES.S_ADMIN && canAddAcl;
  const create = useCreateVoiceWebhook();
  const bodyParams = useVoiceWebhookBodyParameters();

  const initial = useMemo<VoiceWebhookFormValues>(
    () => ({
      webhookName: '',
      url: '',
      event: '',
      markAsDefault: 0,
      authToken: '',
      headers: { 'Content-Type': 'application/json' },
      bodyTable: buildInitialBodyTable(bodyParams.data ?? []),
    }),
    [bodyParams.data],
  );

  // Super-admin still can't add webhooks (unchanged); sub-super-admin now
  // needs the explicit ACL "add" permission on Webhook List instead of a
  // blanket block.
  if (isSubOrSuper && !canAdd) return <Navigate to="/webhook-list" replace />;

  // The form is keyed on the body-table length so re-mounts pick up the
  // populated body params once the lookup query resolves.
  const formKey = `${initial.bodyTable.length}`;

  return (
    <VoiceWebhookForm
      key={formKey}
      mode="create"
      initialValues={initial}
      isPending={create.isPending}
      onSubmit={(payload) => {
        create.mutate(
          {
            ...payload,
            userId: Number(userId),
          },
          {
            onSuccess: (res) => {
              if (res?.message === 'Parameters Incorrect') {
                toast.error(res.message);
                return;
              }
              toast.success(res?.message ?? 'Webhook created');
              navigate('/webhook-list');
            },
            onError: () => toast.error('Could not create the webhook.'),
          },
        );
      }}
    />
  );
}
